const HowTo = () => {
    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 lg:p-8 space-y-8">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 font-mono mb-2">
                    How to use The Catchphraser
                </h1>
                <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
            </div>

            {/* Modes Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-blue-500 pl-4">
                    Two Learning Modes
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                        <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-3">1</div>
                            <h3 className="text-lg font-semibold text-gray-800">Continuous Listening</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                            Listen to audio non-stop with real-time sentence display and progress tracking. 
                            Click words for instant English translations. Pause at any phrase to practice 
                            repeating after the narrator and receive AI pronunciation assessment.
                        </p>
                    </div>

                    <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                        <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mr-3">2</div>
                            <h3 className="text-lg font-semibold text-gray-800">Sentence-by-Sentence</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                            Use play, next, and previous buttons to navigate through sentences. 
                            Click the underline beneath any word to start playback from that point, 
                            perfect for focused practice on specific sections.
                        </p>
                    </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border">
                    <img 
                        alt="Audio interface demonstration" 
                        className="w-full rounded-lg shadow-sm"
                        src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/response.png`} 
                    />
                </div>
            </section>

            {/* Case Study Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-purple-500 pl-4">
                    Case Study: Pavel's Progress
                </h2>
                
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <p className="text-gray-600 mb-6">
                        Pavel (10) practicing English with "Harry Potter". Excerpt practiced:
                    </p>
                    
                    <audio 
                        preload="auto" 
                        controls 
                        className="w-full max-w-md mx-auto mb-6"
                        src="https://www.catchphraser.com/api/uploads/temp/671977801ece29e04da62847/English/Audiobook/J.%20K.%20Rowling/Harry%20Potter%20and%20the%20Chamber%20of%20Secrets/0.34_9.0_Harry%20Potter%20and%20the%20Chamber%20of%20Secrets%20-%20CD1%20-%2006%20-%20Aunt%20Petunia's%20pudding.wav" 
                        type="wav/audio"
                    />

                    <div className="space-y-8">
                        <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                            <h4 className="font-semibold text-gray-800 mb-3">First Attempt</h4>
                            <audio 
                                preload="auto" 
                                controls 
                                className="w-full max-w-md mx-auto mb-4"
                                src="https://www.catchphraser.com/api/uploads/feedback/673e1f4b28b69c1a27f110e8/English/Audiobook/J.%20K.%20Rowling/Harry%20Potter%20and%20the%20Chamber%20of%20Secrets/0.34_9.0_1742758705_Harry%20Potter%20and%20the%20Chamber%20of%20Secrets%20-%20CD1%20-%2006%20-%20Aunt%20Petunia's%20pudding.wav" 
                                type="wav/audio"
                            />
                            <img 
                                alt="Pavel's first attempt assessment" 
                                className="w-full rounded-lg shadow-sm"
                                src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/pasha_screen_00.png`} 
                            />
                        </div>

                        <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                            <h4 className="font-semibold text-gray-800 mb-3">After Two Weeks</h4>
                            <audio 
                                preload="auto" 
                                controls 
                                className="w-full max-w-md mx-auto mb-4"
                                src="https://www.catchphraser.com/api/uploads/feedback/673e1f4b28b69c1a27f110e8/English/Audiobook/J.%20K.%20Rowling/Harry%20Potter%20and%20the%20Chamber%20of%20Secrets/0.34_9.0_1744224051_Harry%20Potter%20and%20the%20Chamber%20of%20Secrets%20-%20CD1%20-%2006%20-%20Aunt%20Petunia's%20pudding.wav" 
                                type="wav/audio"
                            />
                            <img 
                                alt="Pavel's improved assessment" 
                                className="w-full rounded-lg shadow-sm"
                                src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/pasha_screen_01.png`} 
                            />
                        </div>
                    </div>

                    <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-gray-700 font-semibold">
                            ✅ Overall scores for Accuracy, Fluency, Completeness, and Pronunciation showed significant improvement.
                        </p>
                    </div>
                </div>
            </section>

            {/* Assessment Metrics */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-green-500 pl-4">
                    Understanding Assessment Metrics
                </h2>
                
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <p className="text-gray-600 mb-4">
                        Based on <a className="text-blue-600 hover:text-blue-800 font-semibold" target="_blank" rel="noopener noreferrer" href="https://learn.microsoft.com/en-us/azure/ai-services/speech-service/how-to-pronunciation-assessment?pivots=programming-language-python">Microsoft Pronunciation Assessment</a>
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg border">
                            <h4 className="font-semibold text-gray-800 mb-2">🎯 Accuracy</h4>
                            <p className="text-sm text-gray-600">How closely phonemes match native speaker pronunciation</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border">
                            <h4 className="font-semibold text-gray-800 mb-2">💬 Fluency</h4>
                            <p className="text-sm text-gray-600">How well speech matches native speaker's pause patterns</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border">
                            <h4 className="font-semibold text-gray-800 mb-2">📊 Completeness</h4>
                            <p className="text-sm text-gray-600">Ratio of pronounced words to reference text</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border">
                            <h4 className="font-semibold text-gray-800 mb-2">⭐ Overall Score</h4>
                            <p className="text-sm text-gray-600">Combined score based on all assessment metrics</p>
                        </div>
                    </div>

                    <div className="mt-6 p-4 bg-white rounded-lg border">
                        <h4 className="font-semibold text-gray-800 mb-3">Color Coding System</h4>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm">
                            <div className="flex items-center">
                                <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                                <span>80-100% (Excellent)</span>
                            </div>
                            <div className="flex items-center">
                                <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
                                <span>40-79% (Good)</span>
                            </div>
                            <div className="flex items-center">
                                <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                                <span>0-39% (Needs Practice)</span>
                            </div>
                        </div>
                        <p className="text-gray-600 mt-2 text-sm">
                            Applied to individual words and <a className="text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer" href="https://www.britannica.com/topic/phoneme">phonemes</a> (shown as dots)
                        </p>
                    </div>
                </div>
            </section>

            {/* Strategy Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-yellow-500 pl-4">
                    Effective Learning Strategy
                </h2>
                
                <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200 space-y-4">
                    <div className="flex items-start">
                        <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">✓</div>
                        <p className="text-gray-700 flex-1">Repeat phrases 2-3 times to improve results before moving forward</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">✓</div>
                        <p className="text-gray-700 flex-1">Practice regularly for 15-20 minutes daily, like fitness or music training</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">✓</div>
                        <p className="text-gray-700 flex-1">Avoid over-focusing on difficult phonemes - progress takes time</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">✓</div>
                        <p className="text-gray-700 flex-1">Use AI feedback to identify patterns and focus your practice</p>
                    </div>
                </div>
            </section>

            {/* AI Accuracy Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-red-500 pl-4">
                    AI Assessment Reliability
                </h2>
                
                <div className="bg-red-50 rounded-xl p-6 border border-red-200 space-y-4">
                    <div className="flex items-start">
                        <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">🎯</div>
                        <p className="text-gray-700 flex-1">Consistently rates native speakers at 90-100% accuracy range</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">🌍</div>
                        <p className="text-gray-700 flex-1">Identifies language-specific pronunciation challenges accurately</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">⚡</div>
                        <p className="text-gray-700 flex-1">Provides reliable feedback across 20+ sentences despite environmental factors</p>
                    </div>
                </div>
            </section>

            {/* Progress Tracking Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-purple-500 pl-4">
                    Progress & Statistics
                </h2>
                
                <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                    <p className="text-gray-600">
                        Access detailed statistics by clicking your username. Track your improvement with:
                    </p>
                    
                    <div className="space-y-4">
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <h4 className="font-semibold text-gray-800 mb-3">Sentence Accuracy Trends</h4>
                            <img 
                                alt="Sentence accuracy statistics" 
                                className="w-full rounded-lg shadow-sm"
                                src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/stats_sentences.png`} 
                            />
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <h4 className="font-semibold text-gray-800 mb-3">Phoneme Performance</h4>
                            <img 
                                alt="Phoneme accuracy statistics" 
                                className="w-full rounded-lg shadow-sm"
                                src={`${process.env.REACT_APP_USER_API_URL}/uploads/screens/stats_phonemes.png`} 
                            />
                        </div>
                    </div>

                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                        <p className="text-purple-800 font-semibold">🚀 Future Development</p>
                        <p className="text-purple-700 text-sm mt-1">
                            Phoneme statistics will soon power personalized minimal pair exercises to accelerate pronunciation training.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default HowTo;